"use client"

import { observer } from "mobx-react-lite"
import useBackButton from "../../shared/hooks/useBack"
import { useAppContext } from "../../shared/functions/Context"
import { useState, useEffect } from "react"
import ErrorBoundary from "../../shared/components/error-boundary/ErrorBoundary"
import { LoadingEllipsis } from "../../shared/components/loading/Loading"
import useTitle from "../../shared/hooks/useTitle"
import { useNavigate } from "react-router-dom"
import { Clock, TrendingUp, FileText, UserIcon, Building2, CheckCircle } from "lucide-react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../dashboard/company-dashboard/ui/chart"

export interface IDisputeUser {
  userId: string
  userName: string | null
  disputedMeasuresCount: number
  jobTitle: string | null
  userDepartment: string
  submitted: number
}

const DisputeDashboard = observer(() => {
  const { api, store, ui } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [departmentCounts, setDepartmentCounts] = useState<{
    [key: string]: number
  }>({})
  const [filter, setFilter] = useState("all")

  const navigate = useNavigate()

  const allScorecards = store.individualScorecard.all
  const measures = store.measure.all
  const departments = store.department.all

  useTitle("Dispute Dashboard")
  useBackButton()

  const departmentMap = departments.reduce((acc: { [key: string]: string }, department: any) => {
    acc[department.asJson.id] = department.asJson.name
    return acc
  }, {})

  const disputedUids = allScorecards
    .filter((scorecard) => scorecard.asJson.isDispute)
    .map((disputedScorecard) => disputedScorecard.asJson.uid)

  const getDisputeUsers = (): IDisputeUser[] => {
    return allScorecards
      .filter((scorecard) => disputedUids.includes(scorecard.asJson.uid))
      .map((scorecard) => {
        const userId = scorecard.asJson.uid
        const disputedMeasuresCount = measures.filter(
          (measure) =>
            measure.asJson.uid === userId &&
            (measure.asJson.disputeReason || measure.asJson.finalAssessmentDisputeReason),
        ).length
        const user = store.user.all.find((u) => u.asJson.uid === userId)
        const userDepartmentId = user?.asJson.department || "No Department"
        const userDepartment = departmentMap[userDepartmentId] || "Unknown Department"

        return {
          userId,
          userName: user?.asJson.displayName || "Unknown User",
          disputedMeasuresCount,
          jobTitle: user?.asJson.jobTitle || null,
          userDepartment,
          submitted: scorecard.asJson.submittedOn || 0,
        }
      })
  }

  const disputeUsers = getDisputeUsers()

  const notifications = disputeUsers.slice(0, 5).map((user) => ({
    number: user.disputedMeasuresCount,
    person: user.userName || "Unknown User",
    jobTitle: user.jobTitle || "N/A",
    department: user.userDepartment,
    submitted: user.submitted,
  }))

  const countDisputesByDepartment = () => {
    const counts: { [key: string]: number } = {}
    disputeUsers.forEach((user) => {
      counts[user.userDepartment] = (counts[user.userDepartment] || 0) + 1
    })
    return counts
  }

  const resolvedDisputesCount = measures.filter(
    (measure) =>
      measure.asJson.isResolved ===true).length

  const pendingDisputes = measures.filter(
    (measure) =>
      !measure.asJson.isResolved &&
      disputedUids.includes(measure.asJson.uid) &&
      (measure.asJson.disputeReason || measure.asJson.finalAssessmentDisputeReason),
  ).length

  const loadAll = async () => {
    setLoading(true)
    try {
      await api.individualScorecard.getAll()
      await api.user.getAll()
      await api.department.getAll()
      await api.measure.getAll()

      const counts = countDisputesByDepartment()
      setDepartmentCounts(counts)
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Failed to load.",
        type: "danger",
      })
    }
    setLoading(false)
  }

  useEffect(() => {
    loadAll()
  }, [api, ui])

  const totalDisputesLogged = disputedUids.length
  const totalScorecards = allScorecards.length
  const disputePercentage = totalScorecards ? ((totalDisputesLogged / totalScorecards) * 100).toFixed(2) : "0"

  const maxDisputes = Math.max(...Object.values(departmentCounts))
  const departmentsWithMostDisputes = Object.keys(departmentCounts).filter(
    (department) => departmentCounts[department] === maxDisputes,
  )

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === "high") return notification.number > 5
    if (filter === "medium") return notification.number > 2 && notification.number <= 5
    if (filter === "low") return notification.number <= 2
    return true
  })

  const departmentChartData = Object.entries(departmentCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([department, count]) => ({
      department: department.length > 15 ? department.substring(0, 15) + "..." : department,
      disputes: count,
    }))

  const statusChartData = [
    { name: "Resolved", value: resolvedDisputesCount, color: "#4bb543" },
    { name: "Pending", value: pendingDisputes, color: "#faa05a" },
  ]

  const chartConfig = {
    disputes: {
      label: "Disputes",
      color: "hsl(var(--chart-1))",
    },
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingEllipsis />
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div className="w-full">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-[#84cbe9]" />
              </div>
              <h3 className="text-sm font-medium text-gray-600">Total Requests</h3>
            </div>
            <p className="text-3xl font-bold text-[#3f6a8e]">{disputeUsers.length}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <UserIcon className="w-5 h-5 text-[#faa05a]" />
              </div>
              <h3 className="text-sm font-medium text-gray-600">Total Disputes</h3>
            </div>
            <p className="text-3xl font-bold text-[#faa05a]">
              {disputeUsers.reduce((sum, user) => sum + user.disputedMeasuresCount, 0)}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-[#4bb543]" />
              </div>
              <h3 className="text-sm font-medium text-gray-600">Resolved Disputes</h3>
            </div>
            <p className="text-3xl font-bold text-[#4bb543]">{resolvedDisputesCount}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building2 className="w-5 h-5 text-[#9366cc]" />
              </div>
              <h3 className="text-sm font-medium text-gray-600">Departments</h3>
            </div>
            <p className="text-3xl font-bold text-[#9366cc]">
              {new Set(disputeUsers.map((u) => u.userDepartment)).size}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Department Bar Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-[#3f6a8e] mb-6">Disputes by Department</h2>
            {departmentChartData.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={departmentChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="department"
                      tick={{ fill: "#6b7280", fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis tick={{ fill: "#6b7280", fontSize: 12 }} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="disputes" fill="#84cbe9" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                <p>No data available</p>
              </div>
            )}
          </div>

          {/* Status Pie Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-[#3f6a8e] mb-6">Dispute Status Overview</h2>
            {statusChartData.some((d) => d.value > 0) ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-400">
                <p>No data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions Section */}
        {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-[#3f6a8e] mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate("/c/scorecards/dispute/requests")}
              className="p-4 border-2 border-[#84cbe9] rounded-lg hover:bg-blue-50 transition-colors text-left group"
            >
              <FileText className="w-6 h-6 text-[#84cbe9] mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">View All Disputes</h3>
              <p className="text-sm text-gray-600">Review and manage all pending disputes</p>
            </button>

            <button
              onClick={() => navigate("/c/scorecards/dispute/log")}
              className="p-4 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left group"
            >
              <Clock className="w-6 h-6 text-gray-600 mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">Dispute History</h3>
              <p className="text-sm text-gray-600">View resolved and archived disputes</p>
            </button>
          </div>
        </div> */}
      </div>
    </ErrorBoundary>
  )
})

export default DisputeDashboard
